import React, { useState, useEffect } from 'react';
import { getCashTips, getCashTipsByEmail, CashTip } from '../services/cashTipService';
import { getStaff, Staff } from '../services/staffService';
import { formatCurrency } from '../lib/formatters';

interface Props {
  tenantId: string;
  userEmail: string;
  isAdminOrManager: boolean;
}

export const CashTipHistory: React.FC<Props> = ({ tenantId, userEmail, isAdminOrManager }) => {
  const [tips, setTips] = useState<CashTip[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const [data, staff] = await Promise.all([
        isAdminOrManager ? getCashTips(tenantId) : getCashTipsByEmail(tenantId, userEmail),
        getStaff(tenantId)
      ]);
      setStaffList(staff);
      setTips(
        [...data].sort((a, b) => {
          const getTimestamp = (t: CashTip) => {
            if (t.approvalTimestamp?.toDate) return t.approvalTimestamp.toDate().getTime();
            if (t.approvalTimestamp?.seconds) return t.approvalTimestamp.seconds * 1000;
            return new Date(t.tipDate).getTime();
          };
          return getTimestamp(b) - getTimestamp(a);
        })
      );
      setLoading(false);
    };
    fetchData();
  }, [tenantId, userEmail, isAdminOrManager]);

  if (loading) return <div className="p-8 text-center text-gray-500">Loading history...</div>;

  return (
    <div className="card p-6 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900">Cash Tip History</h3>
        <span className="text-xs font-bold text-gray-400 uppercase tracking-widest bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100">
          {tips.length} Submissions
        </span>
      </div>
      <div className="space-y-4">
        {tips.length === 0 && <p className="text-gray-500 text-center py-8">No tips found.</p>}
        {tips.map(tip => {
          const matchedStaff = staffList.find(s => 
            (tip.staffId && s.id === tip.staffId) || 
            (s.email && tip.email && s.email.toLowerCase() === tip.email.toLowerCase())
          );
          const resolvedPosId = tip.posId || matchedStaff?.posId;
          const resolvedStaffId = tip.staffId || matchedStaff?.id;
          const resolvedStaffName = tip.staffName || matchedStaff?.name || tip.email;

          return (
            <div key={tip.id} className="p-4 border rounded-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:bg-gray-50 transition-colors">
              <div className="space-y-1.5 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-bold text-gray-900 text-base">{resolvedStaffName}</span>
                  {resolvedPosId && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-mono font-bold uppercase tracking-wider">
                      POS ID: {resolvedPosId}
                    </span>
                  )}
                  {resolvedStaffId && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-200 text-slate-800 text-[10px] font-mono font-bold tracking-wider" title={`Full Staff ID: ${resolvedStaffId}`}>
                      Staff ID: {resolvedStaffId.slice(0, 8)}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-400 font-medium">Email: {tip.email}</p>
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <span className="font-semibold text-gray-700">{tip.tipDate}</span>
                  <span>•</span>
                  <span>Shift: <span className="font-medium text-gray-700">{tip.shiftName || 'N/A'}</span></span>
                  <span>•</span>
                  <span>Method: <span className="font-medium text-gray-700">{tip.payoutMethod || 'N/A'}</span></span>
                  {tip.id && (
                    <>
                      <span>•</span>
                      <span className="font-mono text-[10px] text-gray-400">Record #{tip.id.slice(0, 8)}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="font-black text-lg text-blue-600">{formatCurrency(tip.amount)}</p>
                <p className={`text-xs font-bold uppercase tracking-wider ${tip.status === 'Approved' ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {tip.status}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
